import { useEffect, useState, useCallback } from "react";
import LoginHistoryTable from "../tables/LoginHistoryTable";
import { getLoginHistory, getLoginSummary } from "../api/loginHistory";
import { ErrorBoundary } from "react-error-boundary";

function ErrorFallback({ error }) {
  return (
    <div className="p-4 bg-red-50 border border-red-400 rounded">
      <h2 className="text-lg font-bold text-red-800">Something went wrong:</h2>
      <pre className="text-red-600">{error.message}</pre>
    </div>
  );
}

export default function LoginHistoryList() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState(""); // Date filter
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 10,
    offset: 0,
  });
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchSummary = useCallback(async () => {
    try {
      const data = await getLoginSummary();
      setSummary(data);
    } catch (err) {
      console.error("Error fetching login summary:", err);
    }
  }, []);

  const refresh = useCallback(async (offset = 0, limit = 10) => {
    setLoading(true);
    setError(null);
    try {
      console.log("===== REFRESH CALLED =====");
      console.log("Requesting - offset:", offset, "limit:", limit);
      const data = await getLoginHistory(limit, offset);
      console.log(
        "API Response - total:",
        data.total,
        "records.length:",
        data.records?.length,
        "offset:",
        data.offset,
        "limit:",
        data.limit,
      );
      console.log("Full API Response:", data);
      setRecords(data.records || []);
      setFilteredRecords(data.records || []);
      setPagination((prev) => ({
        total: data.total || 0,
        limit: limit,
        offset: offset,
      }));
      console.log(
        "Pagination state updated - total:",
        data.total,
        "limit:",
        limit,
        "offset:",
        offset,
      );
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err.message ||
          "Failed to fetch login history",
      );
      console.error("Error fetching login history:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = useCallback(
    (e) => {
      const term = e.target.value.toLowerCase();
      setSearchTerm(term);
      setPagination((prev) => ({ ...prev, offset: 0 }));

      if (!term) {
        setFilteredRecords(records);
      } else {
        const filtered = records.filter((record) => {
          const businessName = (record.business_name || "").toLowerCase();
          const email = (record.email || "").toLowerCase();
          return businessName.includes(term) || email.includes(term);
        });
        setFilteredRecords(filtered);
      }
    },
    [records],
  );

  // Handle date filter change
  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    setPagination((prev) => ({ ...prev, offset: 0 }));
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
    setFilteredRecords(records);
    setPagination((prev) => ({ ...prev, offset: 0 }));
  };

  // Initial load
  useEffect(() => {
    refresh(0, 10);
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNextPage = async () => {
    console.log(
      "Next button clicked - current offset:",
      pagination.offset,
      "limit:",
      pagination.limit,
      "total:",
      pagination.total,
    );
    if (pagination.offset + pagination.limit < pagination.total) {
      const newOffset = pagination.offset + pagination.limit;
      console.log("Moving to offset:", newOffset);
      await refresh(newOffset, pagination.limit);
    }
  };

  const handlePreviousPage = async () => {
    console.log(
      "Previous button clicked - current offset:",
      pagination.offset,
      "limit:",
      pagination.limit,
    );
    if (pagination.offset > 0) {
      const newOffset = Math.max(0, pagination.offset - pagination.limit);
      console.log("Moving to offset:", newOffset);
      await refresh(newOffset, pagination.limit);
    }
  };

  const handleRowsPerPageChange = async (e) => {
    const newLimit = parseInt(e.target.value, 10);
    setRowsPerPage(newLimit);
    // Fetch with new limit and offset 0
    await refresh(0, newLimit);
  };

  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  // Combine search term and selected date to filter client-side
  useEffect(() => {
    let result = records;

    // Text search by business name or email
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((record) => {
        const businessName = (record.business_name || "").toLowerCase();
        const email = (record.email || "").toLowerCase();
        return businessName.includes(term) || email.includes(term);
      });
    }

    // Date filter: compare only YYYY-MM-DD portion of login_time
    if (selectedDate) {
      result = result.filter((record) => {
        if (!record?.login_time) return false;
        const d = new Date(record.login_time);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const recordDate = `${yyyy}-${mm}-${dd}`;
        return recordDate === selectedDate;
      });
    }

    setFilteredRecords(result);
  }, [records, searchTerm, selectedDate]);

  const isFiltered = Boolean(searchTerm || selectedDate);
  const showingStart = isFiltered ? 1 : pagination.offset + 1;
  const showingEnd = isFiltered
    ? filteredRecords.length
    : Math.min(pagination.offset + records.length, pagination.total);
  const totalToShow = isFiltered ? filteredRecords.length : pagination.total;
  const currentPageDisplay = isFiltered ? 1 : currentPage;
  const totalPagesDisplay = isFiltered ? 1 : totalPages;

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className="space-y-3 lg:space-y-4">
        {/* Header Card */}
        <div className="bg-white rounded-lg lg:rounded-xl shadow-sm border border-gray-200 p-3 lg:p-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex-1">
              <h2 className="text-base lg:text-xl font-bold text-gray-900">
                Login History
              </h2>
              <p className="text-xs lg:text-sm text-gray-500 mt-1 hidden sm:block">
                Track all business account logins and access attempts
              </p>
            </div>
            <button
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-3 lg:px-6 py-2 lg:py-2.5 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 w-full sm:w-auto justify-center text-sm lg:text-base"
              onClick={() => refresh(0, rowsPerPage)}
              disabled={loading}
            >
              <svg
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <span>{loading ? "Refreshing..." : "Refresh"}</span>
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 lg:p-6 rounded-lg lg:rounded-xl shadow-sm border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xs lg:text-sm font-semibold text-blue-600 uppercase tracking-wide mb-1">
                    Total Logins
                  </h3>
                  <p className="text-2xl lg:text-3xl font-bold text-blue-700">
                    {summary.total_successful_logins}
                  </p>
                </div>
                <div className="bg-blue-200 p-2 lg:p-3 rounded-lg">
                  <svg
                    className="w-6 h-6 lg:w-8 lg:h-8 text-blue-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 lg:p-6 rounded-lg lg:rounded-xl shadow-sm border border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xs lg:text-sm font-semibold text-red-600 uppercase tracking-wide mb-1">
                    Failed Attempts
                  </h3>
                  <p className="text-2xl lg:text-3xl font-bold text-red-700">
                    {summary.total_failed_logins}
                  </p>
                </div>
                <div className="bg-red-200 p-2 lg:p-3 rounded-lg">
                  <svg
                    className="w-6 h-6 lg:w-8 lg:h-8 text-red-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 lg:p-6 rounded-lg lg:rounded-xl shadow-sm border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xs lg:text-sm font-semibold text-green-600 uppercase tracking-wide mb-1">
                    Unique Accounts
                  </h3>
                  <p className="text-2xl lg:text-3xl font-bold text-green-700">
                    {summary.unique_accounts_logged_in}
                  </p>
                </div>
                <div className="bg-green-200 p-2 lg:p-3 rounded-lg">
                  <svg
                    className="w-6 h-6 lg:w-8 lg:h-8 text-green-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 lg:p-6 rounded-lg lg:rounded-xl shadow-sm border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xs lg:text-sm font-semibold text-purple-600 uppercase tracking-wide mb-1">
                    Most Recent
                  </h3>
                  {summary.most_recent_login ? (
                    <p className="text-sm lg:text-base font-semibold text-purple-700">
                      {new Date(
                        summary.most_recent_login.login_time,
                      ).toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  ) : (
                    <p className="text-sm text-purple-600">No logins yet</p>
                  )}
                </div>
                <div className="bg-purple-200 p-2 lg:p-3 rounded-lg">
                  <svg
                    className="w-6 h-6 lg:w-8 lg:h-8 text-purple-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Date Filter Card */}
        <div className="bg-white rounded-lg lg:rounded-xl shadow-sm border border-gray-200 p-3 lg:p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
            {/* Search by Business/Email */}
            <div>
              <label className="block text-xs lg:text-sm font-semibold text-gray-700 mb-2">
                Search by Business or Email
              </label>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <div className="relative flex-1">
                  <svg
                    className="absolute left-3 top-2.5 lg:top-3 w-4 h-4 lg:w-5 lg:h-5 text-gray-400 pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by business name or email..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="w-full pl-10 pr-3 lg:pr-4 py-2 lg:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 font-medium text-gray-700 text-sm lg:text-base"
                  />
                </div>
                {searchTerm && (
                  <button
                    onClick={clearSearch}
                    className="px-3 lg:px-4 py-2 lg:py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-200 font-medium text-sm lg:text-base whitespace-nowrap"
                  >
                    Clear
                  </button>
                )}
              </div>
              {searchTerm && (
                <p className="text-xs lg:text-sm text-blue-600 font-medium mt-2">
                  Found {filteredRecords.length} matching record
                  {filteredRecords.length !== 1 ? "s" : ""}
                </p>
              )}
            </div>

            {/* Date Filter */}
            <div>
              <label className="block text-xs lg:text-sm font-semibold text-gray-700 mb-2">
                Search by Date
              </label>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  className="px-3 lg:px-4 py-2 lg:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 font-medium text-gray-700 text-sm lg:text-base flex-1 sm:flex-none"
                />
                {selectedDate && (
                  <button
                    onClick={() => {
                      setSelectedDate("");
                      setPagination((prev) => ({ ...prev, offset: 0 }));
                    }}
                    className="px-3 lg:px-4 py-2 lg:py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-200 font-medium text-sm lg:text-base"
                  >
                    Clear
                  </button>
                )}
                {selectedDate && (
                  <span className="text-xs lg:text-sm text-gray-600 font-medium whitespace-nowrap">
                    <span className="text-blue-600 font-semibold">
                      {selectedDate}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-3 lg:px-4 py-2 lg:py-3 rounded-lg shadow-sm text-sm">
            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-2 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-lg lg:rounded-xl shadow-sm border border-gray-200 p-8 lg:p-12">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-10 w-10 lg:h-12 lg:w-12 border-4 border-blue-600 border-t-transparent mb-3 lg:mb-4"></div>
              <p className="text-gray-600 font-medium text-sm lg:text-base">
                Loading login history...
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Unified Data Table Block */}
            <div className="bg-white rounded-lg lg:rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Header with Controls */}
              <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 px-3 lg:px-6 py-3 lg:py-4">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 lg:gap-4">
                  {/* Left: Rows Per Page Selector */}
                  <div className="flex items-center gap-2 lg:gap-3">
                    <label className="text-xs lg:text-sm font-semibold text-gray-700 whitespace-nowrap">
                      Display:
                    </label>
                    <select
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                      className="px-3 lg:px-4 py-2 lg:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 font-medium text-gray-700 text-xs lg:text-sm hover:border-gray-400 bg-white"
                    >
                      <option value="10">10 rows</option>
                      <option value="25">25 rows</option>
                      <option value="50">50 rows</option>
                      <option value="100">100 rows</option>
                    </select>
                  </div>

                  {/* Right: Page Info */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 lg:gap-4 w-full lg:w-auto">
                    <div className="text-xs lg:text-sm text-gray-600 font-medium bg-white px-3 py-1.5 rounded-lg border border-gray-200">
                      <span>
                        Page{" "}
                        <span className="text-blue-600 font-semibold">
                          {currentPageDisplay}
                        </span>{" "}
                        of{" "}
                        <span className="text-blue-600 font-semibold">
                          {totalPagesDisplay}
                        </span>
                      </span>
                    </div>

                    <div className="text-xs lg:text-sm text-gray-600 font-medium bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200 flex items-center gap-1 w-full sm:w-auto justify-center lg:justify-start">
                      <svg
                        className="w-4 h-4 text-blue-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                        <path
                          fillRule="evenodd"
                          d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>
                        Showing{" "}
                        <span className="font-semibold text-blue-700">
                          {showingStart}–{showingEnd}
                        </span>{" "}
                        of{" "}
                        <span className="font-semibold text-gray-700">
                          {totalToShow}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Table Content */}
              <div className="overflow-x-auto">
                <LoginHistoryTable
                  records={filteredRecords}
                  startIndex={pagination.offset}
                />
              </div>

              {/* Footer with Pagination */}
              <div className="border-t border-gray-200 bg-gray-50 px-3 lg:px-6 py-3 lg:py-4">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-3 lg:gap-4">
                  <button
                    onClick={handlePreviousPage}
                    disabled={isFiltered || pagination.offset === 0 || loading}
                    className="px-4 lg:px-6 py-2 lg:py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all duration-200 font-medium flex items-center space-x-2 w-full sm:w-auto justify-center text-sm lg:text-base shadow-sm hover:shadow-md"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                    <span>Previous</span>
                  </button>

                  <span className="text-xs lg:text-sm text-gray-600 font-medium bg-white px-4 py-1.5 rounded-lg border border-gray-200">
                    Page{" "}
                    <span className="font-semibold text-gray-800">
                      {currentPageDisplay}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-gray-800">
                      {totalPagesDisplay}
                    </span>
                  </span>

                  <button
                    onClick={handleNextPage}
                    disabled={
                      isFiltered ||
                      pagination.total === 0 ||
                      pagination.offset + pagination.limit >=
                        pagination.total ||
                      loading
                    }
                    className="px-4 lg:px-6 py-2 lg:py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all duration-200 font-medium flex items-center space-x-2 w-full sm:w-auto justify-center text-sm lg:text-base shadow-sm hover:shadow-md"
                  >
                    <span>Next</span>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </ErrorBoundary>
  );
}
